<template>
  <!-- GDPR S8.2b — listă Mailjet → scop Protecția datelor (contract S8 §3). Același pattern ca maparea șabloanelor:
       tabel server-side, select + Salvează pe rând, feedback inline. Toate listele contului apar, și cele ascunse. -->
  <v-alert type="info" density="compact" class="mb-4">
    {{ $t("ncrm.integrations.mailjet.privacy.lists.hint") }}
  </v-alert>
  <!-- Proprietarul canonic al scopurilor este workspace-ul Protecția datelor (ca la maparea șabloanelor). -->
  <div class="mb-4"><NcrmPrivacyPurposeLink /></div>
  <v-alert v-if="feedback.message" :type="feedback.type" closable class="mb-4" @click:close="clearFeedback">
    {{ feedback.message }}
  </v-alert>
  <v-alert v-if="loadError" type="error" class="mb-4">
    {{ loadError }}
    <template #append>
      <v-btn variant="text" @click="fetchLists">{{ $t("ncrm.integrations.mailjet.privacy.retry") }}</v-btn>
    </template>
  </v-alert>
  <v-data-table-server
    :headers="headers"
    :items="lists"
    :items-length="total"
    :loading="loading"
    v-model:items-per-page="perPage"
    v-model:page="page"
    :items-per-page-options="[25, 50, 100]"
    :no-data-text="$t('ncrm.integrations.mailjet.privacy.lists.noLists')"
    density="comfortable"
    class="elevation-0"
    item-key="id"
  >
    <template #item.name="{ item }">
      <div class="d-flex align-center flex-wrap ga-2 py-1">
        <span>{{ item.name }}</span>
        <v-chip v-if="!item.show_in_crm_ui" size="x-small" color="neutral">
          {{ $t("ncrm.integrations.mailjet.privacy.lists.hidden") }}
        </v-chip>
        <v-chip v-if="item.is_deleted" size="x-small" color="error">
          {{ $t("ncrm.integrations.mailjet.privacy.lists.deleted") }}
        </v-chip>
      </div>
    </template>
    <template #item.privacy_purpose_id="{ item }">
      <!-- Select + Salvează pe un rând de lățime fixă; marcajul stă dedesubt, ca toate câmpurile să fie aliniate. -->
      <div class="py-2">
        <div class="d-flex align-center ga-2">
          <v-select v-model="mappingDraft[item.id]" :items="purposeItems" density="compact"
            hide-details class="ncrm-mailjet-list-purpose__select" :aria-label="$t('ncrm.integrations.mailjet.privacy.dataPurpose')" />
          <v-btn size="small" color="primary" variant="tonal" :loading="savingId === item.id"
            :disabled="!mappingChanged(item) || savingId !== null" @click="saveMapping(item)">
            {{ $t("ncrm.integrations.mailjet.privacy.save") }}
          </v-btn>
        </div>
        <v-chip v-if="isNonMarketing(item)" size="x-small" color="secondary" class="mt-1">
          {{ $t("ncrm.integrations.mailjet.privacy.lists.excludedFromStop") }}
        </v-chip>
      </div>
    </template>
  </v-data-table-server>
</template>

<script>
import axios from "axios";
import NcrmPrivacyPurposeLink from "@/components/ncrm/privacy/NcrmPrivacyPurposeLink.vue";

export default {
  name: "NcrmMailjetListPurposesTab",
  components: { NcrmPrivacyPurposeLink },
  props: {
    accountId: { type: [Number, String], default: null },
  },
  data() {
    return {
      lists: [],
      purposes: [],
      mappingDraft: {},
      total: 0,
      loading: false,
      loadError: "",
      feedback: { type: "success", message: "" },
      savingId: null,
      // Only the latest request may update the table (account switch or fast pagination can reorder responses).
      loadSequence: 0,
      page: 1,
      perPage: 50,
    };
  },
  computed: {
    headers() {
      // The server orders lists by status and name; the table does not offer a sort it cannot apply.
      return [
        { title: this.$t("ncrm.integrations.mailjet.panel.headers.name"), key: "name", sortable: false },
        { title: this.$t("ncrm.integrations.mailjet.panel.headers.subscribers"), key: "subscriber_count", align: "end", sortable: false },
        { title: this.$t("ncrm.integrations.mailjet.privacy.dataPurpose"), key: "privacy_purpose_id", sortable: false },
      ];
    },
    purposeItems() {
      return [
        { title: this.$t("ncrm.integrations.mailjet.privacy.noPurpose"), value: null },
        ...this.purposes.map((purpose) => ({
          title: `${purpose.name} · ${this.$t(`ncrm.integrations.mailjet.privacy.status.${purpose.status}`)}`,
          value: String(purpose.id),
          props: { disabled: purpose.status !== "active" },
        })),
      ];
    },
  },
  watch: {
    accountId: {
      handler() { const changed = this.page !== 1; this.page = 1; if (!changed) this.fetchLists(); },
      immediate: true,
    },
    page() { this.fetchLists(); },
    perPage() { const changed = this.page !== 1; this.page = 1; if (!changed) this.fetchLists(); },
  },
  methods: {
    clearFeedback() { this.feedback = { type: "success", message: "" }; },
    extractError(error, fallbackKey) {
      return error?.response?.data?.error || error?.message || this.$t(fallbackKey);
    },
    // Purpose ids travel as strings so the select compares the saved and the draft value the same way.
    purposeKey(value) { return value == null ? null : String(value); },
    mappingChanged(item) { return (this.mappingDraft[item.id] ?? null) !== this.purposeKey(item.privacy_purpose_id); },
    // A list on a non-marketing purpose (e.g. service communications) is not unsubscribed by a global stop (§3).
    isNonMarketing(item) { return Boolean(item.privacy_purpose_id) && item.privacy_purpose_category !== "marketing"; },
    async fetchLists() {
      if (!this.accountId) return;
      const sequence = ++this.loadSequence;
      this.loading = true;
      this.loadError = "";
      try {
        const params = { account_id: this.accountId, limit: this.perPage, offset: (this.page - 1) * this.perPage };
        const { data } = await axios.get("/ncrm/privacy/configuration/mailjet-lists", { params });
        if (sequence !== this.loadSequence) return;
        this.lists = Array.isArray(data?.lists) ? data.lists : [];
        this.purposes = Array.isArray(data?.purposes) ? data.purposes : [];
        this.mappingDraft = Object.fromEntries(this.lists.map((item) => [item.id, this.purposeKey(item.privacy_purpose_id)]));
        this.total = Number(data?.total || 0);
      } catch (error) {
        if (sequence === this.loadSequence) this.loadError = this.extractError(error, "ncrm.integrations.mailjet.privacy.loadError");
      } finally { if (sequence === this.loadSequence) this.loading = false; }
    },
    async saveMapping(item) {
      this.savingId = item.id;
      this.clearFeedback();
      try {
        const purposeId = this.mappingDraft[item.id] ?? null;
        await axios.put(`/ncrm/privacy/configuration/mailjet-lists/${item.id}`, {
          account_id: this.accountId, purpose_id: purposeId,
        });
        this.feedback = { type: "success", message: this.$t("ncrm.integrations.mailjet.privacy.mappingSaved", { name: item.name }) };
        // Reload so the category marker and the purpose name come from the server.
        await this.fetchLists();
      } catch (error) {
        this.feedback = { type: "error", message: this.extractError(error, "ncrm.integrations.mailjet.privacy.mappingError") };
      } finally { this.savingId = null; }
    },
  },
};
</script>

<style scoped>
.ncrm-mailjet-list-purpose__select { min-width: 220px; }
@media (max-width: 600px) { .ncrm-mailjet-list-purpose__select { min-width: 180px; } }
</style>
